// Sidebar Context for managing sidebar state
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  ReactNode,
  useMemo,
  useCallback,
} from 'react';
import {
  CSS_VAR_SIDEBAR_WIDTH,
  SIDEBAR_COLLAPSE_STORAGE_KEY,
  SIDEBAR_DESKTOP_BREAKPOINT,
  getSidebarWidthPx,
} from '@config/layout';

interface SidebarContextType {
  isOpen: boolean;
  isCollapsed: boolean;
  openSidebar: () => void;
  closeSidebar: () => void;
  toggleSidebar: () => void;
  toggleCollapse: () => void;
  setCollapsed: (collapsed: boolean) => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within SidebarProvider');
  }
  return context;
};

function loadCollapsedPreference(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSE_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function persistCollapsedPreference(collapsed: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SIDEBAR_COLLAPSE_STORAGE_KEY, String(collapsed));
  } catch {
    // ignore quota / private mode
  }
}

function applySidebarWidthCss(isDesktop: boolean, isCollapsed: boolean): void {
  if (typeof document === 'undefined') return;
  const px = getSidebarWidthPx(isDesktop, isCollapsed);
  document.documentElement.style.setProperty(
    CSS_VAR_SIDEBAR_WIDTH,
    isDesktop ? `${px}px` : '0px'
  );
}

interface SidebarProviderProps {
  children: ReactNode;
}

export const SidebarProvider: React.FC<SidebarProviderProps> = ({ children }) => {
  const isDesktopRef = useRef<boolean>(
    typeof window !== 'undefined' ? window.innerWidth >= SIDEBAR_DESKTOP_BREAKPOINT : false
  );

  const [isOpen, setIsOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= SIDEBAR_DESKTOP_BREAKPOINT;
    }
    return false;
  });

  const [isCollapsed, setIsCollapsed] = useState(loadCollapsedPreference);

  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth >= SIDEBAR_DESKTOP_BREAKPOINT;
      isDesktopRef.current = desktop;
      if (desktop) {
        setIsOpen(true);
      }
      applySidebarWidthCss(desktop, isCollapsed);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isCollapsed]);

  useEffect(() => {
    applySidebarWidthCss(isDesktopRef.current, isCollapsed);
  }, [isCollapsed]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const lockBodyScroll = () => {
      const mobile = window.innerWidth < SIDEBAR_DESKTOP_BREAKPOINT;
      document.body.style.overflow = mobile && isOpen ? 'hidden' : '';
    };

    lockBodyScroll();
    window.addEventListener('resize', lockBodyScroll);

    return () => {
      window.removeEventListener('resize', lockBodyScroll);
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || isDesktopRef.current || !isOpen) return;
      setIsOpen(false);
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const openSidebar = useCallback(() => setIsOpen(true), []);

  const closeSidebar = useCallback(() => {
    if (isDesktopRef.current) {
      return;
    }
    requestAnimationFrame(() => {
      setIsOpen(false);
    });
  }, []);

  const toggleSidebar = useCallback(() => {
    if (isDesktopRef.current) {
      return;
    }
    setIsOpen((prev) => !prev);
  }, []);

  const setCollapsed = useCallback((collapsed: boolean) => {
    if (!isDesktopRef.current) return;
    setIsCollapsed(collapsed);
    persistCollapsedPreference(collapsed);
    applySidebarWidthCss(true, collapsed);
  }, []);

  const toggleCollapse = useCallback(() => {
    if (!isDesktopRef.current) return;
    setIsCollapsed((prev) => {
      const next = !prev;
      persistCollapsedPreference(next);
      applySidebarWidthCss(true, next);
      return next;
    });
  }, []);

  const contextValue = useMemo(
    () => ({
      isOpen,
      isCollapsed,
      openSidebar,
      closeSidebar,
      toggleSidebar,
      toggleCollapse,
      setCollapsed,
    }),
    [
      isOpen,
      isCollapsed,
      openSidebar,
      closeSidebar,
      toggleSidebar,
      toggleCollapse,
      setCollapsed,
    ]
  );

  return (
    <SidebarContext.Provider value={contextValue}>
      {children}
    </SidebarContext.Provider>
  );
};
